import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';
import {
  Avatar, Card, Chip, Col, Row,
} from '@openedx/paragon';
import { Timelapse } from '@openedx/paragon/icons';
import { logError } from '@edx/frontend-platform/logging';

import { FormattedMessage } from '@edx/frontend-platform/i18n';
import AdminActionsMenu from './AdminActionsMenu';
import LinkCopiedToast from '../settings/SettingsAccessTab/LinkCopiedToast';
import { PENDING_INVITED_ADMIN_PARAM } from '../AdminRegisterPage';
import { configuration } from '../../config';
import { removeTrailingSlash } from '../../utils';

const renderAdminStatus = (status) => (
  status === 'Pending' ? <Chip iconBefore={Timelapse}>{status}</Chip> : status
);

const OrgInviteAdminCard = ({
  original, onRemoveAdmin,
}) => {
  const { enterpriseSlug } = useParams();
  const {
    name, invitedDate, joinedDate, email, status,
  } = original;
  const [isCopyLinkToastOpen, setIsCopyLinkToastOpen] = useState(false);
  const joinedOrg = joinedDate || invitedDate;
  // The `?pending-invited-admin=true` flag tells AdminRegisterPage to bounce
  // a still-pending invited admin through a logout-then-proxy-login flow
  // that triggers the LMS user save and promotes their pending invite to a
  // real admin role. See docs/decisions/0013-pending-invited-admin-bounce-via-logout.rst.
  const inviteLinkUrl = new URL(
    `/${enterpriseSlug}/admin/register`,
    removeTrailingSlash(configuration.BASE_URL || ''),
  );
  inviteLinkUrl.searchParams.set(PENDING_INVITED_ADMIN_PARAM, 'true');
  const inviteLink = inviteLinkUrl.toString();
  const hasClipboard = !!navigator.clipboard;

  const handleCopyInviteLink = () => {
    if (!hasClipboard || !inviteLink) {
      return;
    }

    const addToClipboard = async () => {
      try {
        await navigator.clipboard.writeText(inviteLink);
        setIsCopyLinkToastOpen(true);
      } catch (error) {
        logError(error);
      }
    };
    addToClipboard();
  };

  const handleCloseLinkCopyToast = () => {
    setIsCopyLinkToastOpen(false);
  };

  return (
    <Card orientation="horizontal">
      <Card.Body>
        <Card.Section className="pb-1">
          <Row className="d-flex flex-row">
            <Col xs={2}>
              <Avatar size="lg" />
            </Col>
            <Col>
              <Row>
                <h3 className="pt-2">{name}</h3>
              </Row>
              <Row>
                <p>{email}</p>
              </Row>
            </Col>
            <Col>
              <h5 className="pt-2 text-uppercase">
                {joinedDate ? (
                  <FormattedMessage
                    id="adminPortal.peopleManagement.joinedOrg"
                    defaultMessage="Joined org"
                    description="Title for people management admin date column when an admin already joined."
                  />
                ) : (
                  <FormattedMessage
                    id="adminPortal.peopleManagement.invitedDate"
                    defaultMessage="Invited date"
                    description="Title for people management admin date column when an admin is still invited."
                  />
                )}
              </h5>
              {joinedOrg}
            </Col>
            <Col>
              <h5 className="pt-2 text-uppercase">
                <FormattedMessage
                  id="adminPortal.peopleManagement.role"
                  defaultMessage="Role"
                  description="Title for people management invite admin Role status."
                />
              </h5>
              {renderAdminStatus(status)}
            </Col>
            <div>
              <AdminActionsMenu
                adminId={original.id}
                onRemove={() => onRemoveAdmin(original)}
                onCopy={handleCopyInviteLink}
              />
            </div>
          </Row>
          <LinkCopiedToast show={isCopyLinkToastOpen} onClose={handleCloseLinkCopyToast} />
        </Card.Section>
      </Card.Body>
    </Card>
  );
};

OrgInviteAdminCard.propTypes = {
  original: PropTypes.shape({
    id: PropTypes.number.isRequired,
    email: PropTypes.string.isRequired,
    name: PropTypes.string,
    joinedDate: PropTypes.string,
    invitedDate: PropTypes.string,
    status: PropTypes.string.isRequired,
  }).isRequired,

  onRemoveAdmin: PropTypes.func.isRequired,
};

export default OrgInviteAdminCard;
